/**
 * Просмотр документа нормативной библиотеки (БЗ-19): оглавление, поиск по тексту, переход к пункту и странице
 * (?p=страница&n=пункт), переключение языка, если у документа два файла. Скан без текстового слоя честно
 * помечен: распознавание выполняет сервер, если оно установлено.
 */
import { type KbNormDoc, NORM_STATUSES } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, ListTree } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { metaQuery, Snippet } from './shared';

interface Search {
  p?: number;
  n?: string;
  q?: string;
  lang?: 'ru' | 'kk';
}
interface PageText {
  page: number;
  text: string;
}
const WINDOW = 3;
const CLAUSE = /^(\d{1,2}(?:\.\d{1,3}){0,4})\.?\s/;

function highlight(line: string, q: string) {
  if (!q) return line;
  const i = line.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return line;
  return (
    <>
      {line.slice(0, i)}
      <mark className="rounded bg-accent/30 px-0.5">{line.slice(i, i + q.length)}</mark>
      {line.slice(i + q.length)}
    </>
  );
}

export function NormReader() {
  const { releaseId } = useParams({ strict: false }) as { releaseId: string };
  const search = useSearch({ strict: false }) as Search;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const meta = useQuery(metaQuery);
  const lang = search.lang;
  const doc = useQuery({
    queryKey: ['kb', 'norm', releaseId, lang ?? ''],
    queryFn: () => api<KbNormDoc>(`/kb/norms/${releaseId}`, { query: { lang } }),
  });
  useCrumbs(doc.data ? [{ label: doc.data.code, title: doc.data.name }] : null, doc.error);
  const set = (patch: Partial<Search>) =>
    navigate({
      to: '/kb/norms/$releaseId',
      params: { releaseId },
      search: (prev: Search) => ({ ...prev, ...patch }),
      replace: true,
    });
  const page = Math.max(1, search.p ?? 1);
  const pages = useQuery({
    queryKey: ['kb', 'norm-pages', releaseId, lang ?? '', page],
    queryFn: () =>
      api<{ pages: PageText[]; total: number }>(`/kb/norms/${releaseId}/pages`, {
        query: { lang, from: page, to: page + WINDOW - 1 },
      }),
    enabled: doc.data?.textState === 'ready',
  });
  const [text, setText] = useState(search.q ?? '');
  const hits = useQuery({
    queryKey: ['kb', 'norm-search', releaseId, lang ?? '', search.q ?? ''],
    queryFn: () =>
      api<Array<{ page: number; n: string | null; snippet: string }>>(`/kb/norms/${releaseId}/search`, {
        query: { q: search.q, lang },
      }),
    enabled: doc.data?.textState === 'ready' && (search.q ?? '').trim().length >= 2,
  });
  const ocr = useMutation({
    mutationFn: () => api<{ queued: boolean }>(`/kb/norms/${releaseId}/ocr`, { method: 'POST', body: {} }),
    onSuccess: (r) => {
      toast(r.queued ? 'Поставлено в очередь на распознавание' : 'Распознавать нечего');
      qc.invalidateQueries({ queryKey: ['kb'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Не получилось', 'danger'),
  });
  const anchor = useRef<HTMLDivElement | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: прокрутка к пункту нужна, когда загрузились страницы
  useEffect(() => {
    anchor.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [pages.data, search.n]);

  if (doc.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (doc.error || !doc.data)
    return (
      <Page>
        <ErrorBox error={doc.error} />
      </Page>
    );
  const d = doc.data;
  const total = d.pages;
  const status = NORM_STATUSES[d.status as keyof typeof NORM_STATUSES];
  return (
    <Page>
      <PageHeader
        title={<span className="[overflow-wrap:anywhere]">{d.code}</span>}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span className="[overflow-wrap:anywhere]">{d.name}</span>
            <Badge tone={d.status === 'active' ? 'success' : d.status === 'check' ? 'warning' : 'neutral'}>
              {status ?? d.status}
            </Badge>
          </span>
        }
        actions={
          <>
            <Link
              to="/norms/releases/$releaseId"
              params={{ releaseId }}
              className="inline-flex h-11 items-center rounded-md border border-line-strong px-4 text-[14px] hover:underline md:h-10"
            >
              Карточка документа
            </Link>
            {d.languages.length > 1 ? (
              <fieldset className="m-0 inline-flex min-w-0 overflow-hidden rounded-md border border-line-strong p-0">
                <legend className="sr-only">Язык</legend>
                {(['ru', 'kk'] as const).map((l) => (
                  <button
                    key={l}
                    type="button"
                    aria-pressed={(d.lang ?? 'ru') === l}
                    onClick={() => set({ lang: l, p: undefined, n: undefined })}
                    className={`h-11 px-4 text-[14px] md:h-10 ${(d.lang ?? 'ru') === l ? 'bg-ink font-semibold text-surface' : 'bg-surface text-ink hover:bg-tile'}`}
                  >
                    {l === 'ru' ? 'Русский' : 'Қазақша'}
                  </button>
                ))}
              </fieldset>
            ) : null}
          </>
        }
      />
      {d.textState !== 'ready' ? (
        <Panel
          title={
            d.textState === 'no_text_layer'
              ? 'Скан без текстового слоя'
              : d.textState === 'no_file'
                ? 'Файла нет'
                : 'Текст не прочитан'
          }
        >
          <p className="mb-3 text-[14px] text-ink">
            {d.textState === 'no_text_layer'
              ? 'В этом файле только картинки страниц, поэтому искать по тексту и строить оглавление нельзя. Нужно распознавание.'
              : d.textState === 'no_file'
                ? 'К карточке документа не приложен файл.'
                : 'Файл не удалось прочитать (формат, размер или хранилище). Откройте карточку документа и скачайте файл.'}
          </p>
          {d.textState === 'no_text_layer' ? (
            d.ocrAvailable ? (
              meta.data?.canEdit ? (
                <Button variant="primary" loading={ocr.isPending} onClick={() => ocr.mutate()}>
                  Поставить на распознавание
                </Button>
              ) : (
                <p className="text-[13px] text-ink-2">
                  Распознавание запускает сотрудник с правом на документы.
                </p>
              )
            ) : (
              <p className="text-[13px] text-ink-2">
                {d.ocrNote ?? 'На сервере нет распознавания'}. Пока оно не установлено, документ остаётся в
                списке «Сканы», текст не подделывается.
              </p>
            )
          ) : null}
        </Panel>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
          <aside className="flex flex-col gap-4">
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                set({ q: text.trim() || undefined });
              }}
            >
              <Input
                type="search"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Найти в тексте"
                aria-label="Поиск по тексту документа"
                className="h-11 md:h-10"
              />
              <Button type="submit">Найти</Button>
            </form>
            {search.q ? (
              <Panel
                title="Найдено"
                meta={hits.data ? `${hits.data.length}` : undefined}
                bodyClassName="max-h-80 overflow-y-auto p-0"
              >
                {hits.isPending ? (
                  <Spinner />
                ) : hits.data?.length ? (
                  hits.data.map((h, i) => (
                    <button
                      // biome-ignore lint/suspicious/noArrayIndexKey: результаты поиска не переставляются
                      key={i}
                      type="button"
                      onClick={() => set({ p: h.page, n: h.n ?? undefined })}
                      className="flex min-h-11 w-full flex-col gap-0.5 border-b border-line px-3 py-2 text-left hover:bg-tile/60"
                    >
                      <span className="text-[12px] text-ink-2">
                        {d.paged ? `страница ${h.page}` : `часть ${h.page}`}
                        {h.n ? ` · пункт ${h.n}` : ''}
                      </span>
                      <span className="text-[13px] text-ink [overflow-wrap:anywhere]">
                        <Snippet text={h.snippet} />
                      </span>
                    </button>
                  ))
                ) : (
                  <p className="px-3 py-4 text-[13px] text-ink-2">В тексте этого документа такого нет.</p>
                )}
              </Panel>
            ) : null}
            <details open className="rounded-lg border border-line bg-surface">
              <summary className="flex min-h-11 cursor-pointer items-center gap-2 px-4 text-[14px] font-semibold">
                <ListTree className="size-4" aria-hidden /> Оглавление
              </summary>
              <nav aria-label="Оглавление" className="max-h-[60dvh] overflow-y-auto border-t border-line">
                {d.toc.length ? (
                  d.toc.map((t) => (
                    <button
                      key={`${t.n}|${t.title}`}
                      type="button"
                      onClick={() => set({ p: t.page ?? 1, n: t.n })}
                      style={{ paddingLeft: `${12 + (t.level - 1) * 12}px` }}
                      className="flex min-h-11 w-full items-start justify-between gap-2 border-b border-line/60 py-2 pr-3 text-left text-[13px] hover:bg-tile/60"
                    >
                      <span className="[overflow-wrap:anywhere]">
                        {t.n && !t.title.startsWith(t.n) ? (
                          <span className="num mr-1.5 text-ink-2">{t.n}</span>
                        ) : null}
                        {t.title}
                      </span>
                      {t.page ? <span className="num shrink-0 text-ink-2">{t.page}</span> : null}
                    </button>
                  ))
                ) : (
                  <p className="px-4 py-4 text-[13px] text-ink-2">
                    Заголовки в тексте не найдены: пользуйтесь поиском.
                  </p>
                )}
              </nav>
            </details>
          </aside>
          <section aria-label="Текст документа" className="min-w-0">
            <div className="mb-3 flex items-center justify-between gap-2">
              <Button
                size="sm"
                icon={<ChevronLeft className="size-4" />}
                disabled={page <= 1}
                onClick={() => set({ p: Math.max(1, page - WINDOW), n: undefined })}
              >
                Назад
              </Button>
              <span className="num text-[13px] text-ink-2">
                {d.paged ? 'страницы' : 'части'} {page}–{Math.min(page + WINDOW - 1, total)} из {total}
              </span>
              <Button
                size="sm"
                disabled={page + WINDOW > total}
                onClick={() => set({ p: page + WINDOW, n: undefined })}
              >
                Дальше <ChevronRight className="size-4" />
              </Button>
            </div>
            {pages.isPending ? <Spinner /> : null}
            {pages.error ? <ErrorBox error={pages.error} /> : null}
            {pages.data?.pages.map((pg) => (
              <article
                key={pg.page}
                className="mb-4 rounded-lg border border-line bg-surface p-4 md:p-6"
                aria-label={`Страница ${pg.page}`}
              >
                <h2 className="mb-3 text-[12px] font-medium text-ink-2">
                  {d.paged ? `Страница ${pg.page}` : `Часть ${pg.page}`}
                </h2>
                {pg.text.split('\n').map((line, i) => {
                  const m = CLAUSE.exec(line);
                  const isAnchor = Boolean(search.n && m && m[1] === search.n && pg.page >= page);
                  return (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: строки страницы не переставляются
                      key={i}
                      ref={isAnchor ? anchor : undefined}
                      className={`py-0.5 text-[14px] leading-6 [overflow-wrap:anywhere] ${isAnchor ? '-mx-2 rounded bg-accent/20 px-2 font-medium' : ''}`}
                    >
                      {highlight(line, search.q ?? '')}
                    </div>
                  );
                })}
              </article>
            ))}
          </section>
        </div>
      )}
    </Page>
  );
}
